import { Icon, Icons, Intent, Size } from "construct-ui";
import moment from "moment";
import TableComponent from "../../components/common/table.component";
import DatePickerComponent from "../../components/date-picker/date-picker.component";
import paginationComponent from "../../components/pagination/pagination.component";
import SelectSiteComponent from "../../components/site/select-site.component";
import SelectUserComponent from "../../components/user/select-user.component";
import { formats as FORMATS, SURVEY_QUESTION_TYPE_CODES } from "../../config/constants";
import surveyModel from "../../models/survey/survey.model";
import ViewBase from "../view-base.view";
import SurveyResponseEditDialog from "./survey-response-edit.dialog";

/**
 * Survey Response View
 * 
 * A page that displays the list of questions and responses of a selected survey
 * 
 * @author Darrus Goh
 */
export default function SurveyResponseView() {
  ViewBase.call(this);

  let isOpen = false;

  const SURVEY_RESPONSE_FORM_ID = 'surveyResponsesForm';

  const DEFAULT_FILTER = {
    date: '',
    selectedSites: [],
    selectedUsers: [],
    questionKey: '',
    pageIndex: 1,
    orderBy: '',
    sortBy: '',
    pageSize: 10,
    totalitems: 0
  };

  let encryptedSurveyId = "";
  let filters = structuredClone(DEFAULT_FILTER);
  let selectedQuestionId = "";
  let selectedResponse = {};
  let surveyQuestions = {};
  let questionOptions = {};
  let responses = [];

  const onSubmit = (e) => {
    // Form validation
    if (!this.validateForm(e)) {
      return false;
    }

    this.showLoading();
    getResponses(toQueryParams(encryptedSurveyId, filters))
      .finally(() => {
        this.hideLoading();
      });;
  };

  const onClear = () => {
    filters = structuredClone(DEFAULT_FILTER);
    let form = document.getElementById(SURVEY_RESPONSE_FORM_ID);
    form.classList.remove('was-validated');
  };

  const toQueryParams = (encryptedSurveyId, params) => {
    return Object.assign(
      {
        SurveyId: encryptedSurveyId,
        PageIndex: params.pageIndex,
        PageSize: params.pageSize,
        OrderBy: params.orderBy,
        SortBy: params.sortBy,
      },
      params.questionKey ? { QuestionKey: params.questionKey } : null,
      params.date ? { Date: moment(params.date, FORMATS.dateFormat).format(FORMATS.apiDateFormat) } : null,
      _.isEmpty(params.selectedSites) ? null : { siteIds: params.selectedSites.map(site => site.code).join('^') },
      _.isEmpty(params.selectedUsers) ? null : { userIds: params.selectedUsers.map(user => user.userID).join('^') }
    );
  };

  const getResponses = (params) => {
    return surveyModel.getResponses(params)
      .then((result) => {
        responses = result.data;
        filters.pageIndex = result.pageIndex;
        filters.pageSize = result.pageSize;
        filters.totalItems = result.total;
      })
      .catch((error) => {
        this.handleError(error);
      });
  };

  const getQuestions = (surveyId) => {
    return surveyModel.getQuestions(surveyId)
      .then((result) => {
        // Extract all questions within main category > sub category and flatten into 1 array
        let allQuestions = result.flatMap((mainCategory) => {
          return mainCategory.subCategoryList.flatMap((subCategory) => {
            return subCategory.questionList;
          });
        });

        // Convert to object map for faster access
        surveyQuestions = allQuestions.reduce((accu, question) => {
          accu[question.surveyQuestionId] = question;
          return accu;
        }, {});
      })
      .catch((error) => {
        this.handleError(error)
      });
  };

  const getQuestionOptions = () => {
    const questionIds = Object.keys(surveyQuestions);
    return surveyModel.getQuestionOptions(questionIds.join('^'))
      .then((result) => {
        const response = result.genericResponse[0];
        response.data.forEach((data) => {
          const option = data.reduce((accu, value, index) => {
            accu[response.headerColumn[index].name] = value;
            return accu;
          }, {});

          if (!questionOptions[option.QuestionId]) {
            questionOptions[option.QuestionId] = {}
          }

          questionOptions[option.QuestionId][option.OptionKey] = option;
        });
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  return {
    oninit: ({ attrs }) => {
      encryptedSurveyId = attrs.surveyId;

      this.showLoading();
      getQuestions(encryptedSurveyId)
        .then(getQuestionOptions)
        .finally(() => { this.hideLoading(); });
    },
    view: ({ attrs }) => {
      return [
        /* TITLE AND BACK BUTTON */
        m('.w-100',
          m('h2',
            m('i.bi.bi-arrow-left.cursor-pointer', {
              onclick: attrs.onGoBack
            }),
            `[Survey Response]-${attrs.surveyTitle}`
          )
        ),
        /* SEARCH */
        m('.card.mb-3',
          m('.card-body',
            m('form.needs-validation[novalidate]', { id: SURVEY_RESPONSE_FORM_ID, onsubmit: onSubmit },
              m('.row.mb-3',
                m('.col-md-3.col-lg-3.position-relative',
                  m("label.form-label.mandatory", this.getText("LT00000739", "Response Date")),
                  m(DatePickerComponent, {
                    class: 'form-control',
                    required: true,
                    date: filters.date,
                    onselect: (e) => {
                      filters.date = moment(e.detail.date).format(FORMATS.dateFormat);
                    }
                  }),
                  m('.invalid-tooltip', this.getText("LT00000729", "This field is required"))
                ),
                m('.col-md-3.col-lg-3.position-relative',
                  m('label.form-label.mandatory', this.getText("LT00000071", "Site")),
                  m(SelectSiteComponent, {
                    selectedItemList: filters.selectedSites,
                    required: true,
                    onChange: (sites) => {
                      filters.selectedSites = sites;
                    }
                  }),
                  m('.invalid-tooltip', this.getText("LT00000729", "This field is required"))
                ),
                m('.col-md-3.col-lg-3.position-relative',
                  m('label.form-label.mandatory', this.getText("LT00000219", "Users")),
                  m(SelectUserComponent, {
                    class: 'form-control',
                    required: true,
                    default: {
                      userStatusCode: '0001'
                    },
                    onSelect: (u) => {
                      filters.selectedUsers = u;
                    },
                    selectedUserList: filters.selectedUsers,
                  }),
                  m('.invalid-tooltip', this.getText("LT00000729", "This field is required"))
                ),
                m('.col-md-3.col-lg-3.position-relative',
                  m('label.form-label.mandatory', this.getText("LT00000738", "Question ID")),
                  m('input', {
                    class: 'form-control form-control-bg',
                    placeholder: this.getText("", "Question ID"),
                    required: true,
                    type: 'text',
                    value: filters.questionKey,
                    onchange: (e) => {
                      filters.questionKey = e.target.value;
                    }
                  }),
                  m('.invalid-tooltip', this.getText("LT00000729", "This field is required"))
                )
              ),
              m('.col.d-flex.justify-content-end.gap-2',
                m('button.btn.btn-primary', { type: 'submit' }, this.getText("LT00000001", "Search")),
                m('button.btn.btn-white', { type: 'button', onclick: onClear }, this.getText("LT00000031", "Clear"))
              )
            )
          )
        ),
        /* TABLE */
        m('.card',
          m('.card-body',
            m(TableComponent, {
              headers: [
                { label: this.getText("LT00000738", "Question ID") },
                { label: this.getText("LT00000358", "Question Text") },
                { label: this.getText("LT00000361", "Question Type") },
                { label: this.getText("LT00000743", "Response") },
                { label: this.getText("LT00000071", "Site") },
                { label: this.getText("LT00000009", "User") },
                { label: this.getText("LT00000739", "Response Date") },
                { label: this.getText("LT00000033", "Actions") },
              ],
              details: responses.map(item => {
                return m('tr',
                  m('td.col-1', item.questionKey),
                  m('td.col-2', item.questionText),
                  m('td.col-1', item.questionType),
                  m('td.col-3.text-wrap', item.questionTypeCode !== SURVEY_QUESTION_TYPE_CODES.DATE
                    ? item.response
                    : moment(item.response).format(FORMATS.dateFormat)
                  ),
                  m('td.col-1', item.site),
                  m('td.col-1', item.promoterId),
                  m('td.col-1', moment(item.surveyYMD).format(FORMATS.dateFormat)),
                  m('td.col-1.center', m(Icon, {
                    name: Icons.EDIT,
                    size: Size.XL,
                    intent: Intent.PRIMARY,
                    disabled: true,
                    onclick: () => {
                      selectedResponse = item;
                      selectedQuestionId = item.questionId;
                      isOpen = true;
                    }
                  })),
                );
              }),
              pagination: m(paginationComponent, {
                currentPage: filters.pageIndex,
                pageSize: filters.pageSize,
                totalItems: filters.totalItems,
                onPageChanged: (pageIndex) => {
                  filters.pageIndex = pageIndex;
                  onSubmit();
                },
                onPageSizeChanged: (pageIndex, pageSize) => {
                  filters.pageSize = pageSize;
                  onSubmit();
                }
              })
            })
          )
        ),
        /* EDIT DIALOG */
        isOpen && m(SurveyResponseEditDialog, {
          encryptedSurveyId,
          selectedResponse,
          surveyQuestions,
          questionOptions,
          selectedQuestionId,
          onSave: () => {
            isOpen = false;
            onSubmit();
          },
          onClose: () => { isOpen = false; }
        }),

        this.getBaseViews()
      ];
    }
  };
}
